/*!

=========================================================
* Comprehend PT - Continue with Microsoft
=========================================================

* A copy of GoogleSignInButton pointed at /v1/auth/microsoft/start. See that
* file for why there is no identity-provider JS SDK here (the extension's MV3
* CSP) and how the backend hands the session back to Home.js.

=========================================================

*/

/* global chrome */

import { useContext } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';

// Login.js polls the opened tab's localStorage for the session while this flag
// is set - the same mechanism the Google, CORA and Confluent SSO buttons use.
export const MICROSOFT_SSO_STARTED = 'MICROSOFT_SSO_STARTED';

const isCX = () => window.location.protocol === 'chrome-extension:';

// True when we're running inside an iframe (the embed widget on an EMR page).
// Microsoft blocks framed sign-in, so open a top-level tab instead.
const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

// Microsoft's four-square mark. Inlined so this component has no asset
// dependency and renders identically in the extension bundle.
const MicrosoftMark = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 21 21"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: '10px', flexShrink: 0 }}
    aria-hidden="true"
  >
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

/**
 * @param {string} label   Button text. Defaults to "Continue with Microsoft".
 * @param {function} onStarted  Called right after the flow is kicked off, so a
 *                              parent (Login.js) can begin polling in the
 *                              extension. Optional.
 */
const MicrosoftSignInButton = ({
  label = 'Continue with Microsoft',
  onStarted,
}) => {
  const { apiClient } = useContext(GlobalContext);

  const handleClick = (e) => {
    e.preventDefault();
    const url = apiClient.microsoftSignInUrl();

    if (isCX()) {
      sessionStorage.setItem(MICROSOFT_SSO_STARTED, 'true');
      chrome.tabs.create({ url });
    } else if (isEmbedded()) {
      const popup = window.open(
        url + '?embedOrigin=' + encodeURIComponent(window.location.origin),
        '_blank',
      );
      if (onStarted) onStarted({ popup });
      return;
    } else {
      window.location.href = url;
    }

    if (onStarted) onStarted();
  };

  return (
    <Button
      block
      color="white"
      type="button"
      onClick={handleClick}
      className="btn-neutral"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #dee2e6',
        fontWeight: 600,
        color: '#525f7f',
      }}
    >
      <MicrosoftMark />
      {label}
    </Button>
  );
};

export default MicrosoftSignInButton;
